import { useEffect, useState } from 'react';
import DateRangePickerWithShortcuts from '../date-range-picker-with-shortcuts/date-range-picker-with-shortcuts';
import { ExportButtons } from '../export-buttons';
import { TabsMenu } from '../../../../components/ui/menu/TabsMenu';
import { PickerRangeValue } from '@mui/x-date-pickers/internals/models';
import dayjs, { Dayjs } from 'dayjs';
import { YearPicker } from './year-picker';

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export function DateRangeFilter({
  options,
  onChange,
  onExportExcel,
  shouldDisableDate,
  initialStartDate,
  initialEndDate,
  exporting,
  pickerMode = 'dateRange',
  disabled,
}: {
  options: { id: string; label: string; startDate: Date; endDate: Date }[];
  onChange?: (dateRange: [Date, Date]) => void;
  onExportExcel?: () => void;
  shouldDisableDate?: (date: Dayjs, startDate: Date | null, endDate: Date | null, position: 'start' | 'end') => boolean;
  initialStartDate?: Date;
  initialEndDate?: Date;
  exporting?: boolean;
  /**
   * Controls the custom-date picker rendered next to the tabs.
   *  - `'dateRange'` (default): MUI date-range picker with shortcuts.
   *  - `'year'`: react-datepicker year picker; selecting a year sets the
   *    range to 1 Jan – 31 Dec of that year.
   */
  pickerMode?: 'dateRange' | 'year';
  disabled?: boolean;
}) {
  const initialMatch =
    initialStartDate && initialEndDate
      ? options.find(
          (option) => isSameDay(option.startDate, initialStartDate) && isSameDay(option.endDate, initialEndDate),
        )
      : undefined;
  const defaultOption = initialMatch ?? options[0];
  const [startDate, setStartDate] = useState<Date | null>(defaultOption.startDate);
  const [endDate, setEndDate] = useState<Date | null>(defaultOption.endDate);
  const [selected, setSelected] = useState(defaultOption.id);

  useEffect(() => {
    if (onChange && startDate && endDate) {
      onChange([startDate, endDate]);
    }
    const selectedOption = options.find(
      (option) =>
        startDate != null &&
        endDate != null &&
        isSameDay(option.startDate, startDate) &&
        isSameDay(option.endDate, endDate),
    );
    setSelected(selectedOption?.id || 'custom');
  }, [startDate, endDate]);

  return (
    <div className="flex gap-2 justify-between">
      <TabsMenu
        options={options}
        selected={selected}
        disabled={disabled}
        onSelect={(id: string) => {
          setSelected(id);
          const selectedOption = options.find((option) => option.id === id);
          if (selectedOption) {
            setStartDate(selectedOption.startDate);
            setEndDate(selectedOption.endDate);
          }
        }}
      />
      <div className={`flex gap-2 ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
        {pickerMode === 'year' ? (
          <YearPicker
            value={startDate}
            onYearChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        ) : (
          <DateRangePickerWithShortcuts
            onChange={(value: PickerRangeValue) => {
              if (value) {
                if (value[0] && value[0].toDate().getTime() !== startDate?.getTime()) {
                  setStartDate(value[0].toDate());
                  setEndDate(null);
                }
                if (value[1] && value[1].toDate().getTime() !== endDate?.getTime()) {
                  setEndDate(value[1].toDate());
                }
              }
            }}
            selectedDate={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
            calendars={2}
            shouldDisableDate={(date, position) => shouldDisableDate?.(date, startDate, endDate, position) || false}
          />
        )}
        <ExportButtons onExportExcel={onExportExcel} loading={exporting} />
      </div>
    </div>
  );
}
